"use client";
import {
  Autocomplete,
  Backdrop,
  Box,
  Checkbox,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TextField,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { FC, useEffect, useState } from "react";
import { SelectArrowIcon } from "./Icons";

interface OptionProps {
  label: string;
  id: string;
  children?: OptionProps[];
}

interface MultiSelectProps {
  defaultValue?: string[];
  options?: OptionProps[];
  onSelect?: (value: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  requireMin?: number;
  name?: string;
  onClose?: (values: string[]) => void;
}

const BpIcon = styled("span")(({ theme }) => ({
  borderRadius: 3,
  width: 16,
  height: 16,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 0 0 1px rgb(16 22 26 / 40%)"
      : "inset 0 0 0 1.5px #B3B6C2, inset 0 -1px 0 rgba(16,22,26,.1)",
  ".Mui-focusVisible &": {
    outline: "2px auto #6270c1",
    outlineOffset: 2,
  },
  "input:disabled ~ &": {
    boxShadow: "none",
    background:
      theme.palette.mode === "dark"
        ? "rgba(57,75,89,.5)"
        : "rgba(206,217,224,.5)",
  },
  "&:before": {
    display: "block",
    width: 16,
    height: 16,
    content: '""',
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "80%",
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fillRule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23b2b8d5'/%3E%3C/svg%3E\")",
  },
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: "#6270c1",
  boxShadow: "none",
  "&:before": {
    display: "block",
    width: 16,
    height: 16,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "80%",
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fillRule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
});

const CustomPaper = styled(Paper)({
  border: "1px solid #ebebf0",
  borderTop: "none",
  borderBottom: "none",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
});

const SubMenu = styled(Paper)({
  position: 'absolute',
  left: '100%',
  top: 0,
  marginLeft: '1px',
  backgroundColor: '#fff',
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  minWidth: '200px',
  zIndex: 2,
  padding: '0px',
});

const ParentItem = styled('li')({
  display: 'flex',
  alignItems: 'center',
  padding: '0px 12px',
  cursor: 'pointer',
  minHeight: 40,
  '&:hover': {
    background: 'linear-gradient(to bottom, #f2f2f5, #eaeaf3)',
  },
});

const ChildItem = styled(MenuItem)({
  padding: '0px 12px',
  minHeight: 40,
  '&:hover': {
    background: 'linear-gradient(to bottom, #f2f2f5, #eaeaf3)',
  },
});

export const MultiSelect: FC<MultiSelectProps> = ({
  defaultValue = [],
  options = [],
  onSelect,
  disabled = false,
  placeholder = "Select",
  requireMin = 0,
  name,
  onClose,
}) => {
  const [value, setValue] = useState<OptionProps[]>([]);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Check if any option in the tree has children
  const hasNestedOptions = (opts: OptionProps[] | undefined): boolean => {
    if (!opts || !Array.isArray(opts) || opts.length === 0) return false;
    return opts.some(opt =>
      opt?.children && (opt.children.length > 0 || hasNestedOptions(opt.children))
    );
  };

  const isNested = hasNestedOptions(options);

  const handleSelect = (_: any, newValue: OptionProps[]) => {
    if (newValue.length >= requireMin) {
      setValue([...newValue]);
      onSelect?.(newValue.map((item) => item.id));
    }
  };

  const handleOptionSelect = (option: OptionProps) => {
    const newValue = value.some(v => v.id === option.id)
      ? value.filter(v => v.id !== option.id)
      : [...value, option];
    handleSelect(null, newValue);
  };

  useEffect(() => {
    if (defaultValue.length) {
      const selected = defaultValue?.map((item) => {
        const findOption = (opts: OptionProps[]): OptionProps | undefined => {
          for (const opt of opts) {
            if (opt.id === item) return opt;
            if (opt.children) {
              const found = findOption(opt.children);
              if (found) return found;
            }
          }
          return undefined;
        };
        return findOption(options) || { id: "", label: "" };
      });

      setValue(selected);
    }
  }, [defaultValue, options]);

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: OptionProps, { selected }: { selected: boolean }) => {
    const hasChildren = option.children && Array.isArray(option.children) && option.children.length > 0;

    return (
      <ParentItem
        {...props}
        onClick={(e: React.MouseEvent<HTMLLIElement>) => {
          if (hasChildren) {
            return;
          };
          e.stopPropagation();
          props.onClick?.(e);
        }}
        onMouseEnter={(e: React.MouseEvent<HTMLLIElement>) => {
          if (hasChildren) {
            setHoveredOption(option.id);
            setAnchorEl(e.currentTarget);
          }
        }}
        onMouseLeave={() => {
          if (!hasChildren) {
            setHoveredOption(null);
            setAnchorEl(null);
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {!isNested && !hasChildren ? (
            <Checkbox
              sx={{
                padding: "0px"
              }}
              disableRipple
              checkedIcon={<BpCheckedIcon />}
              icon={<BpIcon />}
              inputProps={{ "aria-label": "Checkbox demo" }}
              size="small"
              style={{ marginRight: 8 }}
              checked={selected}
            />
          ) : null}
          <Typography>{option.label}</Typography>
          {hasChildren && (
            <SelectArrowIcon
              style={{
                transform: 'rotate(-90deg)',
                marginLeft: 'auto',
              }}
              color="#1B1E3D"
              w={8}
              h={8}
            />
          )}
          {isNested && !hasChildren ? (
            <Checkbox
              sx={{
                padding: "0px"
              }}
              disableRipple
              checkedIcon={<BpCheckedIcon />}
              icon={<BpIcon />}
              inputProps={{ "aria-label": "Checkbox demo" }}
              size="small"
              style={{ marginLeft: 'auto' }}
              checked={selected}
            />
          ) : null}
        </Box>
        {hasChildren && hoveredOption === option.id && option.children && Array.isArray(option.children) && (
          <Popper
            open={true}
            anchorEl={anchorEl}
            placement="right-start"
            sx={{ zIndex: 1301 }}
          >
            <SubMenu>
              <MenuList>
                {option.children.map((child) => {
                  const childHasChildren = child.children && Array.isArray(child.children) && child.children.length > 0;
                  const isChildNested = hasNestedOptions(option.children || []);

                  return (
                    <ChildItem
                      key={child.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOptionSelect(child);
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        {!isChildNested && !childHasChildren ? (
                          <Checkbox
                            disableRipple
                            checkedIcon={<BpCheckedIcon />}
                            icon={<BpIcon />}
                            inputProps={{ "aria-label": "Checkbox demo" }}
                            size="small"
                            style={{ marginRight: 8 }}
                            checked={value.some(v => v.id === child.id)}
                          />
                        ) : null}
                        <Typography>{child.label}</Typography>
                        {childHasChildren && (
                          <SelectArrowIcon
                            style={{
                              transform: 'rotate(-90deg)',
                              marginLeft: 'auto',
                            }}
                            color="#1B1E3D"
                            w={8}
                            h={8}
                          />
                        )}
                        {isChildNested && !childHasChildren ? (
                          <Checkbox
                            disableRipple
                            checkedIcon={<BpCheckedIcon />}
                            icon={<BpIcon />}
                            inputProps={{ "aria-label": "Checkbox demo" }}
                            size="small"
                            style={{ marginLeft: 'auto' }}
                            checked={value.some(v => v.id === child.id)}
                          />
                        ) : null}
                      </Box>
                    </ChildItem>
                  );
                })}
              </MenuList>
            </SubMenu>
          </Popper>
        )}
      </ParentItem>
    );
  };

  return (
    <Box
      sx={{
        ".MuiAutocomplete-root": {
          border: "none !important",
        },
        ".MuiInputBase-root": {
          border: "1px solid #ebebf0",
          borderColor: "#ebebf0 !important",
          borderWidth: "1px !important",
          p: 0,
          minHeight: 40,
          backgroundColor: "#fff!important",
          padding: "0 2px!important",
          zIndex: isMenuOpen ? 2 : 1,
        },
      }}
      onMouseLeave={() => {
        setHoveredOption(null);
        setAnchorEl(null);
      }}
    >
      {isMenuOpen && (
        <Backdrop
          sx={{
            zIndex: 1,
            color: "#fff",
            backgroundColor: "rgba(41, 44, 87, 0.2)",
          }}
          open={isMenuOpen}
        />
      )}
      <Autocomplete
        disableClearable={requireMin > 0}
        PaperComponent={CustomPaper}
        disabled={disabled}
        multiple
        aria-sort="none"
        value={value}
        onOpen={() => setIsMenuOpen(true)}
        onClose={() => {
          setIsMenuOpen(false);
          onClose?.(value.map((item) => item.id));
        }}
        disableCloseOnSelect
        onChange={handleSelect}
        options={options}
        getOptionLabel={(option) => option.label}
        autoComplete={false}
        popupIcon={
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
            }}
          >
            <SelectArrowIcon color="#1B1E3D" w={8} h={8} />
          </Box>
        }
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={!value?.length ? placeholder : ""}
          />
        )}
      />
      <input
        required={requireMin > 0}
        type="hidden"
        name={name}
        value={value.map((item) => item.id)}
      />
    </Box>
  );
};


export function truncateString(str = "", num: number) {
  // If the length of str is less than or equal to num
  // just return str--don't truncate it.
  if (str?.length <= num) {
    return str;
  }
  // Return str truncated with '...' concatenated to the end of str.
  return str?.slice(0, num) + "...";
}
